/*
  Rayvox 3D: the controls around each 3D viewer (dials, meters, tabs, Hold to Blow), ported from the design board's
  wire() (design/store-board/board.src.html), and the loader for the viewers themselves.

  The viewers are heavy (three.js), so they live in a separate module, assets/rayvox-3d-scenes.js, built from
  src/rayvox-3d-scenes.js. Each [data-3d] element names that module (data-scenes) and its model (data-model; the
  Engraving view also data-model-go), all as asset URLs from Liquid, and the module is imported only when the
  viewer comes near the screen. Until it's drawn, and if WebGL isn't there, the viewer's picture shows instead; the
  controls work either way.

  Sections load this file themselves and it registers with assets/rayvox.js through window.RayvoxQueue.
*/

// ---------- The Resono Go's dial (rayvox_cad/designs/resono-go/v019) ----------
// A teardrop slot in the body, covered by the dial's Ø6.5 round hole. DEQ is the open overlap's equivalent diameter
// (mm) for each hole angle from -60° to 150°, from the release's own aperture model. Closed at -53°, widest at 145°.
const DEQ = [
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.23, 0.37, 0.49, 0.59, 0.68, 0.76, 0.83, 0.9, 0.96, 1.02,
  1.07, 1.12, 1.18, 1.22, 1.27, 1.32, 1.36, 1.41, 1.45, 1.49, 1.53, 1.57, 1.61, 1.64, 1.68, 1.72, 1.75, 1.79, 1.82,
  1.85, 1.89, 1.92, 1.95, 1.98, 2.01, 2.04, 2.07, 2.1, 2.13, 2.16, 2.19, 2.22, 2.25, 2.28, 2.31, 2.34, 2.37, 2.4, 2.43,
  2.46, 2.5, 2.53, 2.56, 2.59, 2.62, 2.65, 2.68, 2.71, 2.74, 2.77, 2.81, 2.84, 2.87, 2.9, 2.94, 2.97, 3, 3.04, 3.08,
  3.11, 3.15, 3.18, 3.2, 3.22, 3.24, 3.27, 3.29, 3.31, 3.33, 3.36, 3.39, 3.41, 3.44, 3.47, 3.5, 3.52, 3.56, 3.59, 3.62,
  3.65, 3.68, 3.72, 3.75, 3.79, 3.82, 3.86, 3.9, 3.94, 3.98, 4.02, 4.06, 4.1, 4.14, 4.18, 4.22, 4.27, 4.31, 4.36, 4.4,
  4.44, 4.49, 4.53, 4.58, 4.62, 4.67, 4.72, 4.76, 4.81, 4.86, 4.9, 4.95, 4.99, 5.04, 5.09, 5.13, 5.18, 5.23, 5.27, 5.32,
  5.36, 5.41, 5.45, 5.5, 5.54, 5.59, 5.63, 5.67, 5.71, 5.76, 5.8, 5.84, 5.88, 5.92, 5.96, 6, 6.04, 6.07, 6.11, 6.14,
  6.18, 6.21, 6.24, 6.28, 6.3, 6.33, 6.36, 6.39, 6.41, 6.43, 6.45, 6.47, 6.49, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5,
  6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.49, 6.43, 6.37, 6.31, 6.25, 0,
];
const deqAt = (phi) => {
  const x = Math.max(-60, Math.min(150, phi)) + 60;
  const i = Math.floor(x);
  const f = x - i;
  return DEQ[i] + (DEQ[Math.min(210, i + 1)] - DEQ[i]) * f;
};
const PHI_OPEN = 145;
const PHI_CLOSED = -53;
// The control: 0 is wide open, 1 closed.
const phiOf = (n) => PHI_OPEN - (PHI_OPEN - PHI_CLOSED) * n;
// Lite physics: the overlap is an orifice in series with the voice. A smaller opening holds more pressure behind it,
// lets less air through, and sends what does get out faster; fully covered, nothing flows.
const flowState = (n) => {
  const d = deqAt(phiOf(n));
  const a = (d / 6.5) ** 2;
  if (a <= 0) return { d: 0, a: 0, p: 1, q: 0, v: 0 };
  const R = 1 / (a * a);
  const p = R / (R + 5.67);
  return { d, a, p, q: Math.min(1, (a * Math.sqrt(p)) / 0.388), v: Math.sqrt(p) };
};
const flowWords = (n) => {
  const d = flowState(n).d;
  return d === 0
    ? 'Closed: the dial covers the opening. No air gets through.'
    : d < 1.5
      ? 'Nearly closed: strong back-pressure, a thin, fast stream.'
      : d < 3.6
        ? 'In the middle: gentle back-pressure, a steady stream.'
        : 'Wide open: plenty of air through, light back-pressure.';
};

// ---------- The Resono Pro v2 kit ----------
const VARA_MM = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5];
const VARA_STOPS = VARA_MM.map((_, i) => i / 7);
const PMODES = [
  ['bodies', 'Two Bodies'],
  ['vara', 'VARA'],
  ['spin', 'Spinner'],
  ['bubble', 'Water Bubbling'],
];
const PMETERS = {
  vara: [
    ['Back-Pressure', 'Held behind the hole.'],
    ['Airflow', 'What gets out of the window.'],
  ],
  spin: [
    ['Fan Height', 'Shows your pressure.'],
    ['Fan Speed', 'Shows your airflow.'],
  ],
  bubble: [
    ['Back-Pressure', 'Rises with the depth.'],
    ['Bubbles', 'Follow your airflow.'],
  ],
};
// Shared state for a kit section: the controls write it, the 3D reads it every frame.
const proState = (sec) =>
  (sec.__pro ??= { mode: 'bodies', ext: 2, hole: 3, body: 'go', strength: 0.6, depth: 1 });
const flexState = (sec) => (sec.__flex ??= { bend: 0.35, depth: 1, hold: false, auto: true });

/*
  The dial as a control: a knurled drum like the real one, that you roll left and right (snippets/rayvox-drum.liquid
  prints it). Its mark slides along to show the setting; ticks underneath are the detents, or the VARA's holes.
*/
function wireDrum(el, { stops = null, onInput }) {
  const grip = el.querySelector('[data-grip]');
  const mark = el.querySelector('[data-mark]');
  const body = el.querySelector('.rv-drum__body');
  let v = +el.getAttribute('aria-valuenow') / 100;
  let drag = null;
  const snap = (x) => (stops ? stops.reduce((a, b) => (Math.abs(b - x) < Math.abs(a - x) ? b : a)) : x);
  const show = (x) => {
    grip.style.backgroundPositionX = (-x * 420).toFixed(1) + 'px';
    mark.style.left = (x * 100).toFixed(2) + '%';
    el.setAttribute('aria-valuenow', Math.round(x * 100));
  };
  const set = (x, user = true) => {
    v = Math.max(0, Math.min(1, x));
    show(v);
    if (user) onInput(v);
  };
  body.addEventListener('pointerdown', (e) => {
    body.setPointerCapture(e.pointerId);
    drag = { x: e.clientX, v };
    el.classList.add('is-turning');
  });
  body.addEventListener('pointermove', (e) => {
    if (drag) set(drag.v + (e.clientX - drag.x) / body.clientWidth);
  });
  const end = () => {
    if (!drag) return;
    drag = null;
    el.classList.remove('is-turning');
    if (stops) set(snap(v));
  };
  body.addEventListener('pointerup', end);
  body.addEventListener('pointercancel', end);
  el.querySelectorAll('.rv-drum__scale i').forEach((t) =>
    t.addEventListener('click', () => set(parseFloat(t.style.left) / 100))
  );
  el.addEventListener('keydown', (e) => {
    const d = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }[e.key];
    if (!d) return;
    e.preventDefault();
    if (stops) {
      const i = stops.indexOf(snap(v));
      set(stops[Math.max(0, Math.min(stops.length - 1, i + d))]);
    } else set(v + d * 0.04);
  });
  el.addEventListener(
    'wheel',
    (e) => {
      if (Math.abs(e.deltaX) < Math.abs(e.deltaY) && !e.shiftKey) return;
      e.preventDefault();
      set(v + (e.deltaX || e.deltaY) / 900);
    },
    { passive: false }
  );
  return { set: (x) => set(x, false) };
}

window.RayvoxQueue = window.RayvoxQueue || [];
window.RayvoxQueue.push(({ define, tween, reduce }) => {
  const ctx = { reduce, flowState, phiOf, proState, flexState, PMODES, PMETERS, VARA_MM };

  // Product in 3D (sections/rayvox-demo3d.liquid): the dial control, the meters and the status line. The 3D scene
  // listens for rv:res and rv:xray on the section.
  define('flow', (sec) => {
    const dEl = sec.querySelector('[data-drum]');
    if (!dEl) return;
    const mp = sec.querySelector('[data-mp]');
    const mf = sec.querySelector('[data-mf]');
    const status = sec.querySelector('[data-status]');
    const xr = sec.querySelector('[data-xray]');
    const deq = sec.querySelector('[data-deq]');
    let n = +dEl.getAttribute('aria-valuenow') / 100;
    let touched = false;
    let stop = null;
    let dr = null;
    const setN = (v, user, fromDrum) => {
      if (user) {
        touched = true;
        stop?.();
      }
      n = Math.max(0, Math.min(1, v));
      if (!fromDrum) dr?.set(n);
      dEl.setAttribute('aria-valuetext', flowWords(n));
      const f = flowState(n);
      if (mp) mp.style.width = Math.round(f.p * 100) + '%';
      if (mf) mf.style.width = Math.round(f.q * 100) + '%';
      if (status) status.textContent = flowWords(n);
      if (deq) deq.textContent = f.d > 0 ? `Opening: ${f.d.toFixed(1)} mm` : 'Opening: Closed';
      sec.dispatchEvent(new CustomEvent('rv:res', { detail: n }));
    };
    dr = wireDrum(dEl, { onInput: (v) => setN(v, true, true) });
    sec.__setRes = (v) => setN(v, true);
    sec.__res = () => n;
    xr?.addEventListener('click', () => {
      const on = xr.getAttribute('aria-pressed') !== 'true';
      xr.setAttribute('aria-pressed', String(on));
      sec.__xray = on;
      sec.dispatchEvent(new CustomEvent('rv:xray', { detail: on }));
    });
    setN(n);
    // One demonstration sweep when it first appears, then it's yours.
    if (reduce) return;
    const host = sec.querySelector('.rv-labgrid__head');
    const path = [0.53, 0.95, 0.12, 0.53];
    let seg = 0;
    const next = () => {
      if (touched || seg >= path.length - 1) return;
      const a0 = path[seg];
      const b0 = path[seg + 1];
      seg++;
      stop = tween(1600, (q) => setN(a0 + (b0 - a0) * q), () => setTimeout(next, 400));
    };
    const start = () => setTimeout(next, 1200);
    if (host?.classList.contains('is-in')) start();
    else host?.addEventListener('rv:in', start, { once: true });
  });

  // The Resono Pro kit (sections/rayvox-pro3d.liquid, and the homepage promo): tabs or points pick the set-up; the
  // controls write the shared state.
  define('pro3d', (sec) => {
    const st = proState(sec);
    const panes = [...sec.querySelectorAll('[data-ppanel]')];
    const tabs = [...sec.querySelectorAll('[data-pmode]')];
    const check = (attr, v) =>
      sec.querySelectorAll(`[${attr}]`).forEach((o) => o.setAttribute('aria-checked', String(o.getAttribute(attr) === String(v))));
    // Bubbling needs the Resono at full length to reach the water.
    const pick = (m) => {
      st.mode = m;
      if (m === 'bubble') {
        st.ext = 2;
        check('data-plen', 2);
      }
      tabs.forEach((b) => {
        const on = b.dataset.pmode === m;
        b.setAttribute('aria-selected', String(on));
        b.tabIndex = on ? 0 : -1;
      });
      panes.forEach((p) => (p.hidden = p.dataset.ppanel !== m));
      sec.querySelectorAll('[data-pctl]').forEach((p) => (p.hidden = p.dataset.pctl !== m));
    };
    // It starts on the first set-up the section lists.
    const first = tabs[0]?.dataset.pmode || sec.querySelector('[data-pp]')?.dataset.pp;
    if (first) st.mode = first;
    tabs.forEach((b) => b.addEventListener('click', () => pick(b.dataset.pmode)));
    // Arrow keys move along the tabs, as a tab list should.
    tabs.forEach((b, i) =>
      b.addEventListener('keydown', (e) => {
        const d = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
        if (!d) return;
        e.preventDefault();
        const to = tabs[(i + d + tabs.length) % tabs.length];
        to.focus();
        pick(to.dataset.pmode);
      })
    );
    const radio = (attr, set) =>
      sec.querySelectorAll(`[${attr}]`).forEach((b) =>
        b.addEventListener('click', () => {
          set(b.getAttribute(attr));
          check(attr, b.getAttribute(attr));
        })
      );
    radio('data-plen', (v) => (st.ext = +v));
    radio('data-pbody', (v) => (st.body = v));
    radio('data-pdepth', (v) => (st.depth = +v));
    // The VARA drum: the hole follows the dial as it turns, and settles on the nearest one.
    sec.querySelectorAll('[data-drum="vara"]').forEach((d) =>
      wireDrum(d, {
        stops: VARA_STOPS,
        onInput: (v) => {
          st.hole = Math.round(v * 7);
        },
      })
    );
    // The promo's points: each jumps to its set-up and holds it a while before the cycle moves on.
    sec.querySelectorAll('[data-pp]').forEach((b) =>
      b.addEventListener('click', () => {
        st.mode = b.dataset.pp;
        st.cyc = 0;
        st.holdUntil = sec.rvPause?.still() ? Infinity : performance.now() + 8000;
        if (st.mode === 'bubble') st.ext = 2;
      })
    );
    // The promo's pause (the pauto behaviour in rayvox.js: a picked point, the pause button, keyboard focus) holds
    // the cycle until it's let go (WCAG 2.2.2).
    const holdCycle = (on) => (st.holdUntil = on ? Infinity : 0);
    if (sec.rvPause?.still()) holdCycle(true);
    sec.addEventListener('rv:pause', (e) => holdCycle(e.detail));
    sec.querySelectorAll('[data-pstrength]').forEach((i) =>
      i.addEventListener('input', () => {
        st.strength = 0.15 + (0.85 * i.value) / 100;
        sec.querySelectorAll('[data-pstrength]').forEach((o) => {
          if (o !== i) o.value = i.value;
        });
      })
    );
    // Without the 3D view the meters have nothing to drive them, so they stay hidden; the promo falls back to
    // its photos (the pcycle behaviour in rayvox.js).
    sec.addEventListener('rv:3dfailed', () => {
      sec.querySelectorAll('[data-pbar]').forEach((b) => (b.hidden = true));
      if (sec.hasAttribute('data-pauto')) {
        sec.setAttribute('data-pcycle', '');
        window.Rayvox?.wire(sec);
      }
    });
  });

  // Resono Flex Bubble Lab (sections/rayvox-flex-lab.liquid): hold to blow, bend, depth.
  define('flexlab', (sec) => {
    const st = flexState(sec);
    sec.querySelectorAll('[data-fbend]').forEach((i) => i.addEventListener('input', () => (st.bend = i.value / 100)));
    sec.querySelectorAll('[data-fdepth]').forEach((b) =>
      b.addEventListener('click', () => {
        st.depth = +b.dataset.fdepth;
        sec.querySelectorAll('[data-fdepth]').forEach((o) => o.setAttribute('aria-checked', String(o === b)));
      })
    );
    const hold = (on) => {
      st.hold = on;
      if (on) st.auto = false;
      sec.querySelectorAll('[data-phold]').forEach((b) => b.setAttribute('aria-pressed', String(on)));
    };
    sec.querySelectorAll('[data-phold]').forEach((b) => {
      b.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        b.setPointerCapture?.(e.pointerId);
        hold(true);
      });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((ev) => b.addEventListener(ev, () => hold(false)));
      b.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          hold(true);
        }
      });
      b.addEventListener('keyup', () => hold(false));
      b.addEventListener('blur', () => hold(false));
    });
  });

  // Engraving, Try It (sections/rayvox-engraving.liquid, board: Engraving, B): the words go on the chosen Resono's
  // picture as they're typed, and on the 3D view through rv:eng; the picture, button and note follow the choice.
  // Words longer than the room along the body shrink to fit it, as an engraver would. The button takes the words to
  // that Resono's page for the visit (sessionStorage, never the address), where the engrave behaviour in rayvox.js
  // switches engraving on and fills them in.
  define('engtry', (sec) => {
    const input = sec.querySelector('[data-enginput]');
    const fors = [...sec.querySelectorAll('[data-engfor]')];
    if (!input || !fors.length) return;
    const count = sec.querySelector('[data-engcount]');
    const shots = [...sec.querySelectorAll('[data-engshot]')];
    const txts = [...sec.querySelectorAll('[data-engtxt]')];
    const ctas = [...sec.querySelectorAll('[data-engcta]')];
    const notes = [...sec.querySelectorAll('[data-engnote]')];
    const max = input.maxLength > 0 ? input.maxLength : 14;
    const sample = input.placeholder || 'Your Name';
    let h = (fors.find((b) => b.getAttribute('aria-checked') === 'true') || fors[0]).dataset.engfor;
    const fit = () =>
      txts.forEach((t) => {
        t.style.scale = '';
        const room = t.parentElement.clientWidth;
        const w = t.offsetWidth;
        if (room && w > room) t.style.scale = (room / w).toFixed(3);
      });
    const paint = () => {
      const txt = input.value.trim() || sample;
      txts.forEach((t) => (t.textContent = txt));
      if (count) count.textContent = `${input.value.length} of ${max}`;
      shots.forEach((f) => (f.hidden = f.dataset.engshot !== h));
      ctas.forEach((a) => (a.hidden = a.dataset.engcta !== h));
      notes.forEach((n) => (n.hidden = n.dataset.engnote !== h));
      fit();
      sec.__eng = { h, txt, name: fors.find((b) => b.dataset.engfor === h)?.dataset.name };
      sec.dispatchEvent(new CustomEvent('rv:eng', { detail: sec.__eng }));
    };
    const pick = (b, focus) => {
      fors.forEach((x) => {
        x.setAttribute('aria-checked', String(x === b));
        x.tabIndex = x === b ? 0 : -1;
      });
      if (focus) b.focus();
      h = b.dataset.engfor;
      paint();
    };
    fors.forEach((b) => b.addEventListener('click', () => pick(b)));
    // Arrow keys move along the choice, as a radio group should.
    fors.forEach((b, i) =>
      b.addEventListener('keydown', (e) => {
        const d = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        if (!d) return;
        e.preventDefault();
        pick(fors[(i + d + fors.length) % fors.length], true);
      })
    );
    input.addEventListener('input', paint);
    ctas.forEach((a) =>
      a.addEventListener('click', () => {
        try {
          const words = input.value.trim();
          if (a.dataset.route === 'engrave' && words)
            sessionStorage.setItem('rv-engrave', JSON.stringify({ words, at: Date.now() }));
          else sessionStorage.removeItem('rv-engrave');
        } catch (e) {}
      })
    );
    paint();
    document.fonts?.ready.then(fit);
    // The picture's letters are sized to it, so a new width only needs a new fit once the font is in.
    new ResizeObserver(fit).observe(sec.querySelector('.rv-engtry__shot') || sec);
  });

  // The viewers: load and draw only when near the screen; tear down when the theme editor removes the section.
  define('3d', (el) => {
    const kind = el.dataset['3d'];
    const sec = el.closest('.rv');
    let off = null;
    let dead = false;
    const fail = (err) => {
      el.classList.add('is-failed');
      sec?.dispatchEvent(new CustomEvent('rv:3dfailed'));
      if (err) console.warn('Rayvox 3D:', err);
    };
    const near = new IntersectionObserver(
      (es) => {
        if (!es.some((e) => e.isIntersecting)) return;
        near.disconnect();
        if (!el.dataset.scenes || !el.dataset.model) return fail();
        import(el.dataset.scenes)
          .then((mod) => {
            if (!mod[kind]) throw new Error(`no viewer called ${kind}`);
            return mod[kind](el, ctx);
          })
          .then((stop) => {
            if (dead) stop();
            else off = stop;
          })
          .catch(fail);
      },
      { rootMargin: '300px' }
    );
    near.observe(el);
    const unload = (e) => {
      if (!e.target.contains(el)) return;
      dead = true;
      near.disconnect();
      off?.();
      document.removeEventListener('shopify:section:unload', unload);
    };
    document.addEventListener('shopify:section:unload', unload);
  });
});
